import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { COLORS, TEXTURES, PRODUCTS, translateColor, translateTexture, translateProductDesc, useProducts, STYLES } from '../store/productData';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import { API_URL, calculateMaterials, createQuote, registerAndQuote, getInstallateurs } from '../lib/api';
import type { CalcRequest, CalcResponse, Installateur } from '../lib/api';
import SEOHead from '../components/SEOHead';
import HousePreview3D from '../components/HousePreview3D';
import LoginModal from '../components/LoginModal';
import type { CalcData } from '../components/HousePreview3D';
import ProductDetailModal from '../components/ProductDetailModal';
import { Check, Save, Loader2, CheckCircle2, FileText as FileTextIcon, Info, Heart } from 'lucide-react';
import { addFavorite, removeFavorite, getMyFavorites } from '../lib/api';
import { DiscountBadge, DiscountSavingsStrip, resolveDiscount } from '../components/DiscountBadge';

const CDN = 'https://public.youware.com/users-website-assets/prod/4bc0e411-7ad1-4783-a0a6-898692ab4e6e';

/** Parse "240 x 71 x 15 mm" → { lengthMm, heightMm }. Falls back to 240×71. */
function parseDimensions(dim: string): { lengthMm: number; heightMm: number } {
  const m = dim.match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (m) return { lengthMm: parseInt(m[1], 10), heightMm: parseInt(m[2], 10) };
  return { lengthMm: 240, heightMm: 71 };
}


// Wens Franky (19/8): uitleg-tooltips poppen DIRECT bij hover (geen
// browser-vertraging van title=), kort gehouden, en altijd herkenbaar aan
// een klein vraagteken zodat bezoekers weten dat er iets achter zit.
function Vraagje({ tekst }: { tekst: string }) {
  return (
    <span className="relative inline-flex group/tip align-middle ml-1">
      <span className="w-3.5 h-3.5 rounded-full bg-[#c47a3a] text-white text-[9px] font-bold flex items-center justify-center cursor-help select-none">?</span>
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/tip:block bg-[#1a1a1a] text-white text-[10px] leading-snug rounded-lg px-2.5 py-1.5 w-44 z-50 text-left shadow-lg normal-case font-normal tracking-normal">{tekst}</span>
    </span>
  );
}

export default function ProductsPage() {
  const { products } = useProducts();
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedTexture, setSelectedTexture] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // ── Customer favorites ──────────────────────────────────────────────────────
  // Logged-in users can mark products as favorites. This drives the heart icon
  // overlay on each product card and powers the "Mijn Favorieten" tab in the
  // customer dashboard.
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  // Toast voor favorieten — kort bericht onder in scherm
  const [favToastMsg, setFavToastMsg] = useState<string | null>(null);
  const favToastTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!user) {
      // Niet-ingelogd: laad selectie uit localStorage zodat heart-klikken
      // tussen sessies bewaard blijven (en sync na login).
      try {
        const raw = localStorage.getItem('gvs-pending-favorites');
        const ids = raw ? JSON.parse(raw) as string[] : [];
        setFavoriteIds(new Set(ids));
      } catch { setFavoriteIds(new Set()); }
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        // 1. Sync eventuele lokale (pre-login) favorites NAAR de server
        let pending: string[] = [];
        try {
          const raw = localStorage.getItem('gvs-pending-favorites');
          if (raw) pending = JSON.parse(raw) as string[];
        } catch { /* ignore */ }
        if (pending.length > 0) {
          // adminFavorites idempotent toevoegen — fouten negeren
          await Promise.allSettled(pending.map(id => addFavorite(id)));
          try { localStorage.removeItem('gvs-pending-favorites'); } catch { /* ignore */ }
          if (pending.length > 1) {
            setFavToastMsg(`✓ ${pending.length} favorieten gesynchroniseerd`);
            setTimeout(() => setFavToastMsg(null), 3000);
          }
        }
        // 2. Server is nu authoritative — laad de definitieve lijst
        const list = await getMyFavorites();
        if (!cancelled) setFavoriteIds(new Set(list.map(f => f.product_id)));
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // ─── Favorites toggle — werkt voor ingelogd EN niet-ingelogd ──────────────
  //
  // Vroeger: heart-klik zonder login → opende loginmodal (gevoel van "jump").
  // Nu: keuze wordt eerst lokaal opgeslagen (localStorage), met een korte
  // bevestigings-toast. Bij volgende login worden lokale favorieten
  // automatisch gesynced naar de server (zie syncLocalFavorites useEffect).
  const LOCAL_FAVS_KEY = 'gvs-pending-favorites';
  const readLocalFavs = (): string[] => {
    try {
      const raw = localStorage.getItem(LOCAL_FAVS_KEY);
      return raw ? JSON.parse(raw) as string[] : [];
    } catch { return []; }
  };
  const writeLocalFavs = (ids: string[]) => {
    try { localStorage.setItem(LOCAL_FAVS_KEY, JSON.stringify(ids)); } catch { /* quota */ }
  };

  const showFavToast = (msg: string) => {
    setFavToastMsg(msg);
    if (favToastTimer.current) window.clearTimeout(favToastTimer.current);
    favToastTimer.current = window.setTimeout(() => setFavToastMsg(null), 3000);
  };

  const toggleFavorite = async (productId: string) => {
    const isFav = favoriteIds.has(productId);
    // Optimistic UI eerst
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(productId); else next.add(productId);
      return next;
    });

    if (!user) {
      // Niet-ingelogd: bewaar lokaal + toon toast met inlog-uitnodiging
      const current = new Set(readLocalFavs());
      if (isFav) current.delete(productId); else current.add(productId);
      writeLocalFavs([...current]);
      showFavToast(isFav
        ? '✓ Verwijderd uit uw selectie'
        : '❤ Toegevoegd! Log in om uw favorieten op te slaan in uw account.');
      return;
    }

    try {
      if (isFav) await removeFavorite(productId);
      else await addFavorite(productId);
      showFavToast(isFav ? '✓ Verwijderd uit favorieten' : '❤ Toegevoegd aan uw favorieten');
    } catch {
      // Revert on failure
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (isFav) next.add(productId); else next.delete(productId);
        return next;
      });
      showFavToast('Kon niet opslaan — probeer opnieuw');
    }
  };

  // ── Calculator state ─────────────────────────────────────────────────────────
  // Wens Franky (19/8): een klik op het logo (of eender welke navigatie)
  // mag een half opgebouwde offerte NIET wissen. De configurator-toestand
  // wordt per tabblad bewaard (sessionStorage) en bij terugkeer hersteld;
  // tabblad sluiten = schone lei.
  const cfg = (() => {
    try { return JSON.parse(sessionStorage.getItem('gvs-configurator-v1') || 'null'); }
    catch { return null; }
  })();
  const [calcProductId, setCalcProductId] = useState<string>(cfg?.calcProductId ?? 'p1');
  const [area, setArea] = useState<number>(cfg?.area ?? 50);
  const [jointWidth, setJointWidth] = useState<number>(cfg?.jointWidth ?? 10);
  const [cornerLength, setCornerLength] = useState<number>(cfg?.cornerLength ?? 0);
  const [cornerCount, setCornerCount] = useState<number>(cfg?.cornerCount ?? 0);
  // V5.34: snijverlies is een vast percentage dat Filip instelt — de klant
  // ziet het wel maar kan er niet aan draaien.
  const [cuttingLoss, setCuttingLoss] = useState(10);
  // V5.36: /steen/<slug> linkt naar /?product=<slug>#calculator — voorselecteer.
  useEffect(() => {
    const slug = new URLSearchParams(window.location.search).get('product');
    if (!slug) return;
    const prod = products.find(pp => pp.slug === slug);
    if (prod) { setCalcProductId(prod.id); setSelectedProductId(prod.id); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetch(`${API_URL}/api/calculator/instellingen`)
      .then(r => r.json())
      .then(j => { if (j?.ok && Number.isFinite(j.data?.cutting_loss_pct)) setCuttingLoss(j.data.cutting_loss_pct); })
      .catch(() => { /* val terug op 10% */ });
  }, []);
  const [includeGlue, setIncludeGlue] = useState<boolean>(cfg?.includeGlue ?? true);
  const [includeGrout, setIncludeGrout] = useState<boolean>(cfg?.includeGrout ?? true);
  const [groutColor, setGroutColor] = useState<string>(cfg?.groutColor ?? 'grijs');
  const [savedMsg, setSavedMsg] = useState('');

  // API result state
  const [apiResult, setApiResult] = useState<CalcResponse['data'] | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Quote request state
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const pendingQuoteRef = useRef(false);
  const [detailProduct, setDetailProduct] = useState<import('../store/productData').Product | null>(null);

  // Save quote state (save without requesting contact)
  const [saveLoading, setSaveLoading] = useState(false);
  const pendingSaveRef = useRef(false);

  // Inline quote form state
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [qfName, setQfName] = useState('');
  const [qfEmail, setQfEmail] = useState('');
  const [qfPhone, setQfPhone] = useState('');
  const [qfAddress, setQfAddress] = useState('');
  const [qfPostalCity, setQfPostalCity] = useState('');
  const [qfType, setQfType] = useState<'particulier' | 'bedrijf'>('particulier');
  // V6 punt 10: projecttermijn is verplicht (of expliciet 'enkel ter info').
  const [qfTermijn, setQfTermijn] = useState<'' | '3m' | '6m' | '1j' | 'meer' | 'info'>('');
  const [qfCompany, setQfCompany] = useState('');
  const [qfBtw, setQfBtw] = useState('');
  const [qfInstaller, setQfInstaller] = useState<'self' | 'friend' | 'installer' | 'groothandel' | ''>('');
  // V6 punten 12-17: installateurs uit de regio (±50 km) + eigen groothandel
  const [qfInstLijst, setQfInstLijst] = useState<Installateur[] | null>(null);
  const [qfInstSel, setQfInstSel] = useState<string[]>([]);
  const [qfInstLaden, setQfInstLaden] = useState(false);
  const [qfGhNaam, setQfGhNaam] = useState('');
  const [qfGhPlaats, setQfGhPlaats] = useState('');
  const [qfGhEmail, setQfGhEmail] = useState('');

  // Regiolijst ophalen zodra 'installateur' gekozen is en er een postcode staat.
  const qfPostcode4 = (qfPostalCity.match(/\b\d{4}\b/) || [''])[0];
  useEffect(() => {
    if (qfInstaller !== 'installer' || !qfPostcode4) { setQfInstLijst(null); return; }
    let dood = false;
    setQfInstLaden(true);
    getInstallateurs(qfPostcode4)
      .then(l => { if (!dood) setQfInstLijst(l); })
      .catch(() => { if (!dood) setQfInstLijst([]); })
      .finally(() => { if (!dood) setQfInstLaden(false); });
    return () => { dood = true; };
  }, [qfInstaller, qfPostcode4]);
  const [qfErrors, setQfErrors] = useState<Record<string, string>>({});
  const [qfSubmitting, setQfSubmitting] = useState(false);
  const [qfSuccess, setQfSuccess] = useState(false);
  const [qfServerError, setQfServerError] = useState('');

  // v3.2.76 — Filip's wens: bij klik op het logo moet de offerte-invul-
  // strook dichtgaan. Navbar dispatchet 'gvs:reset-calculator', wij
  // sluiten het formulier en clearen errors. Geldt zowel voor open
  // formulier als voor success-state na verzending.
  useEffect(() => {
    const handler = () => {
      setShowQuoteForm(false);
      setQfSuccess(false);
      setQfServerError('');
      setQfErrors({});
    };
    window.addEventListener('gvs:reset-calculator', handler);
    return () => window.removeEventListener('gvs:reset-calculator', handler);
  }, []);

  // Uit de API-lijst (products) zodat prijzen uit de database komen;
  // useProducts valt zelf terug op de hardcoded lijst als de API plat ligt.
  const calcProduct = products.find(p => p.id === calcProductId) || products[0] || PRODUCTS[0];

  const activeProductId = selectedProductId || products[0]?.id || '';

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (selectedColor && p.color !== selectedColor) return false;
      if (selectedTexture && p.texture !== selectedTexture) return false;
      if (search) {
        const q = search.toLowerCase();
        const translatedColor = translateColor(p.color, t).toLowerCase();
        const translatedTexture = translateTexture(p.texture, t).toLowerCase();
        const translatedDesc = translateProductDesc(p.id, t).toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !translatedDesc.includes(q) && !translatedColor.includes(q) && !translatedTexture.includes(q)) return false;
      }
      return true;
    });
  }, [products, selectedColor, selectedTexture, search, t]);

  // Get selected product for 3D preview — follows product grid selection,
  // but falls back to calculator product when nothing is selected in the grid.
  const selectedProduct = products.find(p => p.id === activeProductId) || products[0];
  const previewBrickColor = selectedProduct?.colorHex || calcProduct.colorHex || '#a0522d';

  const houseLabels = {
    title: t('products.3d.title'),
    rotate: t('products.3d.rotate'),
    download: t('products.3d.download'),
    uploadTeaser: t('products.3d.uploadTeaser'),
    uploadCta: t('products.3d.uploadCta'),
    roofLabel: t('products.3d.roofLabel'),
    roofFlat: t('products.3d.roofFlat'),
    roofTiles: t('products.3d.roofTiles'),
    roofSlate: t('products.3d.roofSlate'),
    nightMode: t('products.3d.nightMode'),
    dayMode: t('products.3d.dayMode'),
    dragHint: t('products.3d.dragHint'),
    roofToggle: t('products.3d.roofToggle'),
    roofToggleClose: t('products.3d.roofToggleClose'),
    materialsLabel: t('products.3d.materialsLabel'),
    materialsWall: t('products.3d.materialsWall'),
    materialsWindows: t('products.3d.materialsWindows'),
    materialsNet: t('products.3d.materialsNet'),
    gardenClick: t('products.3d.gardenClick'),
  };

  const scrollToProducts = () => {
    document.getElementById('products-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToCalculator = () => {
    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
  };

  // When user clicks a product in the grid → also pre-select it in the calculator
  const handleProductSelect = (id: string) => {
    setSelectedProductId(id);
    setCalcProductId(id);
  };

  // ── Local (fallback) calculation — uses real product dimensions ──────────────
  const calc = useMemo(() => {
    const { lengthMm: stoneW, heightMm: stoneH } = parseDimensions(calcProduct.dimensions);
    const jw = jointWidth;
    const effectiveStoneW = stoneW + jw, effectiveStoneH = stoneH + jw;
    const stonesPerM2 = (1000 / effectiveStoneW) * (1000 / effectiveStoneH);
    const lossMultiplier = 1 + (cuttingLoss / 100);
    const totalStones = Math.ceil(Math.ceil(area * stonesPerM2) * lossMultiplier);
    const totalCornerPieces = Math.ceil(cornerCount * cornerLength * (1000 / effectiveStoneH) * lossMultiplier);
    const glueKg = includeGlue ? Math.ceil(area * 5 * lossMultiplier) : 0;
    const glueBags = Math.ceil(glueKg / 25);
    const groutKg = includeGrout ? Math.ceil(area * 1.5 * (jw / 10) * lossMultiplier) : 0;
    const groutBags = Math.ceil(groutKg / 25);
    const stonePrice = area * calcProduct.pricePerM2;
    const cornerPrice = totalCornerPieces * (calcProduct.pricePerM2 * 2 / stonesPerM2);
    const gluePrice = glueBags * 18;
    const groutPrice = groutBags * 22;
    const totalPrice = stonePrice + cornerPrice + gluePrice + groutPrice;
    return { stonesPerM2: Math.round(stonesPerM2 * 10) / 10, totalStones, totalCornerPieces, glueKg, glueBags, groutKg, groutBags, stonePrice, cornerPrice, gluePrice, groutPrice, totalPrice, area };
  }, [area, jointWidth, cornerLength, cornerCount, cuttingLoss, includeGlue, includeGrout, calcProduct]);

  // ── API call (debounced) ─────────────────────────────────────────────────────
  const fetchApiCalc = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setApiLoading(true);
      try {
        const req: CalcRequest = {
          productId: calcProductId,
          surfaceM2: area,
          jointWidth,
          cuttingLoss: cuttingLoss / 100,
          corners: cornerCount,
          includeGlue,
          includeGrout,
        };
        const data = await calculateMaterials(req);
        setApiResult(data);
      } catch {
        setApiResult(null); // API down → keep local fallback
      } finally {
        setApiLoading(false);
      }
    }, 600);
  }, [calcProductId, area, jointWidth, cuttingLoss, cornerCount, includeGlue, includeGrout]);

  useEffect(() => {
    fetchApiCalc();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fetchApiCalc]);

  // Merge: prefer API result for stone counts if available
  const displayCalc = useMemo(() => {
    if (apiResult) {
      const s = apiResult.stones;
      const acc = apiResult.accessories;
      const totals = apiResult.totals;
      return {
        stonesPerM2: s.stonesPerM2,
        totalStones: s.stonesNeeded,
        totalCornerPieces: calc.totalCornerPieces,
        glueKg: acc.glue.bags * 25,
        glueBags: acc.glue.bags,
        groutKg: acc.grout.bags * 25,
        groutBags: acc.grout.bags,
        stonePrice: totals.totalExclVat != null ? totals.totalExclVat - (acc.glue.total + acc.grout.total + calc.cornerPrice) : calc.stonePrice,
        cornerPrice: calc.cornerPrice,
        gluePrice: acc.glue.total,
        groutPrice: acc.grout.total,
        totalPrice: totals.totalExclVat != null ? totals.totalExclVat + calc.cornerPrice : calc.totalPrice,
        area,
      };
    }
    return calc;
  }, [apiResult, calc, area]);

  const groutColorMap: Record<string, string> = { grijs: t('calc.grey'), wit: t('calc.white'), antraciet: t('calc.anthracite'), zand: t('calc.sand') };

  // ── v5 blok 4: meerdere gevelstenen in één offerte ───────────────────────────
  // Elke "Vul nog een gevelsteen in" bevriest de huidige berekening als regel;
  // de calculator blijft staan voor de volgende steen. Eén offerte, één klant.
  interface QuoteLine {
    productId: string;
    productName: string;
    colorHex: string;
    voegMm?: number;
    cornerCount?: number;
    cornerLength?: number;
    includeGlue?: boolean;
    includeGrout?: boolean;
    groutColor?: string;
    area: number;
    deliveryWeeks: number | null;
    totalPrice: number;
    note: string;
  }
  const [quoteLines, setQuoteLines] = useState<QuoteLine[]>(cfg?.quoteLines ?? []);

  // Elke wijziging meteen bewaren (zie uitleg bij cfg hierboven).
  useEffect(() => {
    try {
      sessionStorage.setItem('gvs-configurator-v1', JSON.stringify({
        calcProductId, area, jointWidth, cornerLength, cornerCount,
        includeGlue, includeGrout, groutColor, quoteLines,
      }));
    } catch { /* opslag vol/geblokkeerd — geen ramp */ }
  }, [calcProductId, area, jointWidth, cornerLength, cornerCount, includeGlue, includeGrout, groutColor, quoteLines]);
  // V6 punt 1-4: instellingen + berekening zitten in een popup per steen;
  // het hoofdblok is één venster met stenen + 3D-huis.
  const [stoneModal, setStoneModal] = useState(false);
  // Wens Franky (19/8): na '+ Toevoegen' verdwijnt het instellingen-vak en
  // blijft alleen het berekening-overzicht hangen ('overzicht'-fase); elke
  // heropening van de popup start terug in de 'instellingen'-fase.
  const [popupFase, setPopupFase] = useState<'instellingen' | 'overzicht'>('instellingen');
  const formatEuro = (n: number) => '€' + Math.round(n).toLocaleString('nl-BE');
  // v5.21: maximaal 5 stenen per offerte (beslissing Filip/Franky) — ook
  // server-side afgedwongen in alle offerte-routes.
  const MAX_STONES = 5;
  // Fix Franky (19/8): dezelfde steen kan geen twee keer in de offerte —
  // staat de actieve steen al als regel in de lijst, dan telt hij niet
  // nog eens mee (totaal, aantal, items, 3D).
  const actieveTeltMee = !quoteLines.some(l => l.productId === calcProductId);
  const stonesInQuote = quoteLines.length + (actieveTeltMee ? 1 : 0);
  const combinedTotal = quoteLines.reduce((s, l) => s + l.totalPrice, 0)
    + (actieveTeltMee ? displayCalc.totalPrice : 0);

  const buildCurrentLineNote = () => [
    `Product: ${calcProduct.name}`,
    `Kleur: ${calcProduct.color} | Textuur: ${calcProduct.texture}`,
    `Oppervlakte: ${area} m²`,
    `Voegbreedte: ${jointWidth}mm`,
    `Snijverlies: ${cuttingLoss}%`,
    includeGrout ? `Voegkleur: ${groutColorMap[groutColor] || groutColor}` : null,
    cornerCount > 0 ? `Hoeken: ${cornerCount} × ${cornerLength}m (${displayCalc.totalCornerPieces} st)` : null,
    `Stenen: ${displayCalc.totalStones} st (${displayCalc.stonesPerM2}/m²)`,
    includeGlue ? `Lijm: ${displayCalc.glueBags} zakken (${displayCalc.glueKg} kg) — €${displayCalc.gluePrice}` : null,
    includeGrout ? `Voegsel: ${displayCalc.groutBags} zakken (${displayCalc.groutKg} kg) — €${displayCalc.groutPrice}` : null,
    displayCalc.totalCornerPieces > 0 ? `Hoekstenen: €${Math.round(displayCalc.cornerPrice)}` : null,
    `Baksteenstrips: €${Math.round(displayCalc.stonePrice)}`,
    calcProduct.deliveryWeeks != null ? `Levertermijn: ±${calcProduct.deliveryWeeks} weken` : null,
    `Subtotaal excl. BTW: €${Math.round(displayCalc.totalPrice)}`,
  ].filter(Boolean).join(' | ');

  const addAnotherStone = () => {
    if (stonesInQuote >= MAX_STONES) {
      alert(`Maximaal ${MAX_STONES} gevelstenen per offerte.`);
      return;
    }
    const regel: QuoteLine = {
      productId: calcProductId,
      productName: calcProduct.name,
      colorHex: calcProduct.colorHex,
      voegMm: jointWidth,
      cornerCount, cornerLength, includeGlue, includeGrout, groutColor,
      area,
      deliveryWeeks: calcProduct.deliveryWeeks ?? null,
      totalPrice: displayCalc.totalPrice,
      note: buildCurrentLineNote(),
    };
    // Fix Franky (19/8): zelfde steen nooit twee keer — bestaat de regel al,
    // dan wordt hij bijgewerkt met de nieuwe instellingen.
    const bestaat = quoteLines.some(l => l.productId === calcProductId);
    showFavToast(bestaat
      ? `✓ ${calcProduct.name} bijgewerkt in uw offerte (${area} m²)`
      : `✓ ${calcProduct.name} (${area} m²) toegevoegd aan uw offerte — kies nu uw volgende steen`);
    setQuoteLines(ls => {
      const i = ls.findIndex(l => l.productId === calcProductId);
      if (i >= 0) { const kopie = [...ls]; kopie[i] = regel; return kopie; }
      return [...ls, regel];
    });
    // Calculator blijft staan voor de volgende steen — scroll naar productkeuze.
    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
  };
  const removeQuoteLine = (idx: number) => setQuoteLines(ls => ls.filter((_, i) => i !== idx));
  // Wens Franky (19/8): klik op een bevroren steen-chip -> die steen komt
  // terug in het instellingenvenster (met zijn eigen oppervlakte, voeg,
  // hoeken en toebehoren); de steen waar je mee bezig was, wordt op zijn
  // beurt netjes als regel bevroren. Niets gaat verloren.
  const bewerkQuoteLine = (idx: number) => {
    const l = quoteLines[idx];
    if (!l) return;
    const huidige: QuoteLine = {
      productId: calcProductId,
      productName: calcProduct.name,
      colorHex: calcProduct.colorHex,
      voegMm: jointWidth,
      cornerCount, cornerLength, includeGlue, includeGrout, groutColor,
      area,
      deliveryWeeks: calcProduct.deliveryWeeks ?? null,
      totalPrice: displayCalc.totalPrice,
      note: buildCurrentLineNote(),
    };
    // Fix Franky (19/8): de lijst verschuift NIET — elke steen houdt zijn
    // plaats en nummer. Nogmaals klikken op de open rij klapt het
    // instellingen-vak gewoon weer dicht (toggle).
    if (stoneModal && popupFase === 'instellingen' && calcProductId === l.productId) {
      setPopupFase('overzicht');
      return;
    }
    // Een nieuwe unieke steen in opbouw eerst achteraan bewaren zodat hij
    // niet verloren gaat; bestaande regels blijven onaangeroerd staan.
    if (actieveTeltMee) setQuoteLines(ls => [...ls, huidige]);
    setCalcProductId(l.productId);
    setSelectedProductId(l.productId);
    setArea(l.area);
    setJointWidth(l.voegMm ?? 10);
    setCornerCount(l.cornerCount ?? 0);
    setCornerLength(l.cornerLength ?? 0);
    setIncludeGlue(l.includeGlue ?? true);
    setIncludeGrout(l.includeGrout ?? true);
    setGroutColor(l.groutColor ?? 'grijs');
    (setPopupFase('instellingen'), setStoneModal(true));
    showFavToast(`✏️ ${l.productName} terug geopend om aan te passen`);
  };

  /** Alle regels (bevroren + huidige) als items[] voor de API. */
  const buildQuoteItems = () => [
    ...quoteLines.map(l => ({ product_id: l.productId, qty_m2: l.area })),
    ...(actieveTeltMee ? [{ product_id: calcProductId, qty_m2: area }] : []),
  ];

  // ── Build detailed notes for backend ──────────────────────────────────────────
  const buildQuoteNotes = () => {
    const parts: string[] = [];
    quoteLines.forEach((l, i) => parts.push(`STEEN ${i + 1} — ${l.note}`));
    parts.push(quoteLines.length > 0 ? `STEEN ${quoteLines.length + 1} — ${buildCurrentLineNote()}` : buildCurrentLineNote());
    parts.push(`Totaal excl. BTW: €${Math.round(combinedTotal)}`);
    parts.push(`Totaal incl. BTW: €${Math.round(combinedTotal * 1.21)}`);
    // Vaste regel op elke offerte (roadmap blok 4.5 — beslissing Filip).
    parts.push('Werkuren van de plaatsingen niet inbegrepen.');
    return parts.join(' || ');
  };

  // ── Quote request (offerte aanvragen — asks for contact) ────────────────────
  const handleRequestQuote = async () => {
    // ALTIJD een formulier tonen — ook voor ingelogde klanten — zodat ze
    // hun gegevens kunnen bevestigen of aanvullen voor ze de offerte versturen.
    // Profielgegevens prefillen waar beschikbaar.
    if (user) {
      const u = user as { name?: string; email?: string; phone?: string; address?: string;
                          postal_code?: string; city?: string; company_name?: string; btw_number?: string };
      setQfName(u.name ?? '');
      setQfEmail(u.email ?? '');
      setQfPhone(u.phone ?? '');
      setQfAddress(u.address ?? '');
      setQfPostalCity([u.postal_code, u.city].filter(Boolean).join(' '));
      if (u.btw_number || u.company_name) {
        setQfType('bedrijf');
        setQfCompany(u.company_name ?? '');
        setQfBtw(u.btw_number ?? '');
      } else {
        setQfType('particulier');
      }
    }
    setShowQuoteForm(true);
    // Klein scroll-naar trigger zodat het formulier in beeld komt
    setTimeout(() => {
      const el = document.getElementById('quote-form-anchor');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const doCreateQuote = async () => {
    setQuoteLoading(true);
    setQuoteError('');
    try {
      await createQuote({
        customer_name: user!.name,
        customer_email: user!.email,
        total_amount: Math.round(combinedTotal * 100) / 100,
        items: buildQuoteItems(),
        notes_nl: buildQuoteNotes(),
      });
      setQuoteSuccess(true);
      // Redirect logged-in users to dashboard after short delay
      if (user) {
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setTimeout(() => setQuoteSuccess(false), 6000);
      }
    } catch {
      setQuoteError('Aanvraag mislukt. Probeer later opnieuw.');
      setTimeout(() => setQuoteError(''), 5000);
    } finally {
      setQuoteLoading(false);
    }
  };

  // ── Save quote (opslaan — no contact flow, just persist) ────────────────────
  const handleSaveQuote = async () => {
    if (!user) {
      pendingSaveRef.current = true;
      setLoginModalOpen(true);
      return;
    }
    await doSaveQuote();
  };

  const doSaveQuote = async () => {
    setSaveLoading(true);
    try {
      await createQuote({
        customer_name: user!.name,
        customer_email: user!.email,
        total_amount: Math.round(combinedTotal * 100) / 100,
        items: buildQuoteItems(),
        notes_nl: buildQuoteNotes(),
      });
      setSavedMsg('Offerte opgeslagen! Bekijk in uw account.');
      setTimeout(() => setSavedMsg(''), 5000);
    } catch {
      setSavedMsg('Opslaan mislukt. Probeer later opnieuw.');
      setTimeout(() => setSavedMsg(''), 5000);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLoginSuccess = async () => {
    if (pendingQuoteRef.current) {
      pendingQuoteRef.current = false;
      await doCreateQuote();
    }
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      await doSaveQuote();
    }
  };

  // ── Inline quote form validation + submit ─────────────────────────────────
  // B2B regel: zodra bedrijfsnaam OF BTW is ingevuld, MOETEN beide ingevuld
  // zijn (anders is het onvolledig en kan Filip geen factuur uitschrijven).
  const validateQuoteForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!qfTermijn) errs.termijn = t('v6.termijnFout');
    if (!qfName.trim()) errs.name = 'Naam is verplicht';
    if (!qfEmail.trim()) errs.email = 'E-mail is verplicht';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(qfEmail.trim())) errs.email = 'Ongeldig e-mailadres';
    if (!qfPhone.trim()) errs.phone = 'Telefoon is verplicht';
    if (!qfAddress.trim()) errs.address = 'Adres is verplicht';
    if (!qfPostalCity.trim()) errs.postalCity = 'Postcode + Gemeente is verplicht';
    // B2B optioneel: enkel valideren als één van de twee is ingevuld
    const hasCompany = qfCompany.trim().length > 0;
    const hasBtw     = qfBtw.trim().length > 0;
    if (hasCompany && !hasBtw) {
      errs.btw = 'BTW-nummer is verplicht als u een bedrijfsnaam invult';
    }
    if (hasBtw && !hasCompany) {
      errs.company = 'Bedrijfsnaam is verplicht als u een BTW-nummer invult';
    }
    // Lichte BTW-format check (BE0XXX.XXX.XXX of BE 0XXX XXX XXX). Niet strikt
    // — VIES-validatie volgt later, dit is enkel een typo-vangnet.
    if (hasBtw && !/^[A-Z]{2}\s*\d[\d\s.]{7,}$/i.test(qfBtw.trim())) {
      errs.btw = 'Format: landcode + cijfers (bv. BE0123.456.789)';
    }
    if (!qfInstaller) {
      errs.installer = 'Kies een plaatsings-optie zodat wij weten of u een installateur nodig heeft';
    }
    setQfErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleQuoteFormSubmit = async () => {
    if (!validateQuoteForm()) return;
    setQfSubmitting(true);
    setQfServerError('');
    try {
      const res = await registerAndQuote({
        name: qfName.trim(),
        email: qfEmail.trim(),
        phone: qfPhone.trim(),
        address: qfAddress.trim(),
        postal_code_city: qfPostalCity.trim(),
        is_b2b: qfType === 'bedrijf',
        company_name: qfType === 'bedrijf' ? qfCompany.trim() : undefined,
        btw_number: qfType === 'bedrijf' ? qfBtw.trim() : undefined,
        quote_total: Math.round(combinedTotal * 100) / 100,
        items: buildQuoteItems(),
        quote_notes: buildQuoteNotes(),
        installer_choice: qfInstaller === 'groothandel' ? null : (qfInstaller || null),
        installateur_ids: qfInstaller === 'installer' && qfInstSel.length ? qfInstSel : null,
        eigen_groothandel: qfInstaller === 'groothandel' && qfGhNaam.trim()
          ? { naam: qfGhNaam.trim(), plaats: qfGhPlaats.trim() || undefined, email: qfGhEmail.trim() || undefined }
          : null,
        project_termijn: qfTermijn || null,
        plaatsing: qfInstaller === 'installer' ? 'installateur' : qfInstaller === 'self' ? 'zelf' : qfInstaller === 'groothandel' ? 'groothandel' : null,
      });
      // Auto-login: update auth context
      setUser({
        id: res.user.id,
        name: res.user.name,
        email: res.user.email,
        role: res.user.role,
        phone: res.user.phone,
        company_name: res.user.company_name,
        btw_number: res.user.btw_number,
        address: res.user.address,
        city: res.user.city,
        postal_code: res.user.postal_code,
      });
      setQfSuccess(true);
      setTimeout(() => navigate('/dashboard'), 3000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Aanvraag mislukt. Probeer later opnieuw.';
      setQfServerError(msg);
    } finally {
      setQfSubmitting(false);
    }
  };

  // Toggle component for glue/grout
  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
    <div className="relative flex-shrink-0 cursor-pointer" onClick={() => onChange(!checked)}>
      <div className={`w-7 h-4 rounded-full transition-colors ${checked ? 'bg-[#c47a3a]' : 'bg-gray-200'}`} />
      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-transform ${checked ? 'translate-x-3' : ''}`} />
    </div>
  );

  return (
    <div className="pt-16 md:pt-[72px] flex-1 flex flex-col">
      <SEOHead page="home" />

      {/* HERO — V5.32: uniforme banner-hoogte over alle menu-items */}
      <section className="relative h-[84px] md:h-[190px] flex items-center overflow-hidden">
        <div className="absolute inset-0"
          style={{ backgroundImage: `url(${CDN}/6a1e4db4bd1b47a8ad4889b1a1e4d94c.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
        {/* Gsm (Franky 19/8): iets meer ademruimte links en rechts in de hero */}
        <div className="relative z-10 max-w-7xl mx-auto px-7 sm:px-6 lg:px-8 w-full">
          <div className="max-w-xl">
            <div className="hidden md:inline-flex items-center gap-2 px-3 py-1 bg-[#c47a3a]/20 border border-[#c47a3a]/30 rounded-full text-[#c47a3a] text-[10px] font-semibold tracking-wider uppercase mb-1.5">
              <span className="font-bold">BE</span> {t('home.heroBadge')}
            </div>
            <h1 className="text-lg md:text-3xl font-bold text-white leading-[1.1] mb-0 md:mb-1.5" style={{ fontFamily: 'Playfair Display' }}>
              {t('home.heroTitle')} <em className="not-italic text-[#c47a3a]">{t('home.heroTitleAccent')}</em>
            </h1>
            <p className="hidden md:block text-sm md:text-base text-gray-300 leading-relaxed max-w-md">
              {t('home.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* 3D house — mobile only, inside calculator section below */}

      {/* Calculator */}
      <section id="calculator" className="bg-white border-b border-gray-100 py-2 sm:py-4 lg:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section header */}
          <div className="mb-1.5 sm:mb-5">
            {/* V6 punt 7: prominenter op desktop; op gsm klein gehouden
                 zodat de stenen en het huisje meteen in beeld staan. */}
            <h2 className="text-base sm:text-2xl md:text-4xl font-bold text-[#1a1a1a] text-center sm:text-left" style={{ fontFamily: 'Playfair Display' }}>
              {t('home.calcTitle')}
            </h2>
            {/* Uitlegzin verwijderd op vraag Franky (19/8): de kop zegt genoeg. */}
          </div>

          {/* V6 (wens Filip/Franky): één hoofdblok — stenenkiezer + 3D-huis
               samen; instellingen en berekening zitten in de steen-popup. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">

              {/* Stenenkiezer */}
              <div className="h-[420px] lg:h-[460px] flex flex-col overflow-hidden bg-[#faf7f3] rounded-xl p-4 border border-gray-100">
                {/* Kopregel (PRODUCTKEUZE + chips) verwijderd op vraag Franky
                     (19/8): meer stenen in beeld i.p.v. uitleg. */}
                {/* v5 blok 3: lift/scroll zodat álle stenen bereikbaar blijven
                     wanneer de catalogus groeit. */}
                <div className="steengrid grid grid-cols-3 sm:grid-cols-4 gap-2 flex-1 min-h-0 overflow-y-auto pr-1 content-start">
                  {products.map(p => {
                    const discount = resolveDiscount(p);
                    const newPrice = discount ? Math.round(p.pricePerM2 * (1 - discount.pct / 100) * 100) / 100 : null;
                    // Wens Franky (19/8): stenen die in de offerte zitten moeten
                    // ONMISKENBAAR gekozen ogen — en meteen weer normaal zodra
                    // ze uit de offerte gehaald worden (kruisje in de chips-balk).
                    const inOfferte = calcProductId === p.id || quoteLines.some(l => l.productId === p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => {
                          // Wens Franky (19/8): klik op een tegel toont eerst de
                          // grote productfiche (foto + specificaties); van daaruit
                          // ga je met één knop naar de offerte-instellingen.
                          setCalcProductId(p.id); setSelectedProductId(p.id); setDetailProduct(p);
                        }}
                        className={`group flex flex-col items-center gap-1 p-1.5 rounded-lg border text-left transition-all ${
                          inOfferte
                            ? 'border-2 border-[#c47a3a] bg-[#c47a3a]/20 shadow-md'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="w-full h-12 rounded overflow-hidden relative">
                          <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                          {inOfferte && (
                            <div className="absolute inset-0 bg-[#c47a3a]/25 pointer-events-none" />
                          )}
                          {inOfferte && (
                            <div className="absolute top-0.5 right-0.5 w-5 h-5 bg-[#c47a3a] rounded-full flex items-center justify-center shadow">
                              <Check size={11} className="text-white" strokeWidth={3} />
                            </div>
                          )}
                          {/* v3.2.115 — Korting-badge terug op de foto op
                              Filip's tweede verzoek ('je moet die kortings
                              percentages wel nog zien in de foto's'). De
                              doorgestreepte prijs blijft weg om geen
                              verkeerde verwachtingen bij eindklant te
                              wekken — alleen het zichtbare pill-icoontje. */}
                          {discount && (
                            <div className={`absolute ${inOfferte ? 'bottom-0.5 left-0.5' : 'top-0.5 right-0.5'}`}>
                              <DiscountBadge product={p} size="sm" />
                            </div>
                          )}
                          {/* Favorite (heart) — top-left, visible on hover or when favorited */}
                          <div
                            role="button"
                            aria-label="Toevoegen aan favorieten"
                            onClick={(e) => { e.stopPropagation(); void toggleFavorite(p.id); }}
                            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full flex items-center justify-center cursor-pointer transition-all ${
                              favoriteIds.has(p.id)
                                ? 'bg-red-500 opacity-100'
                                : 'bg-black/40 hover:bg-red-500 opacity-0 group-hover:opacity-100'
                            }`}
                          >
                            <Heart size={9} className="text-white" fill={favoriteIds.has(p.id) ? 'white' : 'none'} strokeWidth={2} />
                          </div>
                          {/* Info — bottom-right */}
                          <div
                            onClick={(e) => { e.stopPropagation(); setDetailProduct(p); }}
                            className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-black/40 hover:bg-[#c47a3a] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <Info size={8} className="text-white" />
                          </div>
                        </div>
                        <span className={`text-[10px] block truncate w-full text-center ${inOfferte ? 'font-bold text-[#8a4423]' : 'font-semibold'}`}>{p.name}</span>
                        {/* v3.2.109 — Doorgestreepte korting-prijs weg. Toon
                            altijd één neutrale catalogprijs op de voorkant. */}
                        <span className="text-[9px] text-[#c47a3a] font-bold">€{p.pricePerM2}/m²</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Vlak 2: 3D huis — één instantie (Safari-limiet); vult het
                   volledige vlak, uitleg als overlay zodat de hoogte exact
                   gelijk blijft aan de andere vlakken. */}
              <div id="huis3d" className="h-[420px] lg:h-[460px] relative rounded-xl overflow-hidden">
                <div className="absolute inset-0 [&>*]:h-full">
                  <HousePreview3D
                    brickColor={previewBrickColor}
                    labels={houseLabels}
                    stenen={[
                      ...quoteLines.map(l => ({ kleur: l.colorHex, voegMm: l.voegMm ?? 10 })),
                      ...(actieveTeltMee ? [{ kleur: calcProduct.colorHex, voegMm: jointWidth }] : []),
                    ]}
                  />
                </div>
                <p className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] text-white bg-black/55 px-2.5 py-1 rounded-full whitespace-nowrap pointer-events-none">
                  {t('products.tapToPreview') || 'Klik op het nummer van de gekozen steen'}
                </p>
              </div>
          </div>

          {/* V6: chips-balk — gekozen stenen + totaal + popup-knop */}
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#c47a3a]/30 bg-[#fffaf5] px-3 py-2.5">
            {quoteLines.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 bg-white border border-gray-200 hover:border-[#c47a3a] rounded-full text-xs font-semibold">
                <button onClick={() => bewerkQuoteLine(i)} className="inline-flex items-center gap-1.5" title="Klik om deze steen terug te openen en zijn instellingen aan te passen">
                  <span className="w-5 h-5 rounded-full bg-[#c47a3a] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                  <img src={PRODUCTS.find(pp => pp.id === l.productId)?.images[0] ?? ''} alt="" className="w-6 h-6 rounded-full object-cover" />
                  {l.productName} · {l.area} m² <span className="text-[#c47a3a]">✎</span>
                </button>
                <button onClick={() => removeQuoteLine(i)} className="text-red-600 font-bold px-0.5" title="Verwijder deze steen">✕</button>
              </span>
            ))}
            {/* Wens Franky (19/8): alles terug naar nul — geen voorkeuze */}
            {quoteLines.length > 0 && (
              <button
                onClick={() => {
                  if (!confirm(t('v6.allesWissenBevestig'))) return;
                  setQuoteLines([]);
                  setArea(50); setJointWidth(10); setCornerCount(0); setCornerLength(0);
                  setIncludeGlue(true); setIncludeGrout(true); setGroutColor('grijs');
                  try { sessionStorage.removeItem('gvs-configurator-v1'); } catch { /* ok */ }
                  showFavToast(t('v6.allesGewist'));
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 border border-gray-200 hover:border-red-300 hover:text-red-600 rounded-full text-xs font-semibold text-[#8a7e74]"
                title="Wis alle gekozen stenen en instellingen — start van nul"
              >
                {t('v6.allesWissen')}
              </button>
            )}
            {actieveTeltMee && (
            <button
              onClick={() => (setPopupFase('instellingen'), setStoneModal(true))}
              className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 bg-[#c47a3a]/10 border border-[#c47a3a]/50 rounded-full text-xs font-bold text-[#8a4423]"
              title="Open het venster met instellingen en berekening van de huidige steen"
            >
              <span className="w-5 h-5 rounded-full bg-[#c47a3a] text-white text-[10px] font-bold flex items-center justify-center">{quoteLines.length + 1}</span>
              <img src={calcProduct.images[0]} alt="" className="w-6 h-6 rounded-full object-cover" />
              {calcProduct.name} · {area} m² — {t('v6.instellingenPrijs')}
            </button>
            )}
            <span className="ml-auto text-sm font-bold text-[#1a1a1a]">
              {t('v6.totaal')}: <span className="text-[#c47a3a]">{formatEuro(combinedTotal)}</span> excl.
              <span className="text-xs font-normal text-[#8a7e74]"> · {formatEuro(combinedTotal * 1.21)} incl.</span>
            </span>
            <button
              onClick={() => (setPopupFase('overzicht'), setStoneModal(true))}
              className="px-4 py-2 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded-lg text-xs font-bold"
              title="Open de berekening en vraag uw offerte aan"
            >
              {t('v6.offerteAanvragen')}
            </button>
          </div>

          {/* V6: popup per steen — instellingen + berekening + offerte */}
          {stoneModal && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto p-3 sm:p-6" onClick={() => setStoneModal(false)}>
            <div className="max-w-2xl mx-auto my-4 space-y-4" onClick={e => e.stopPropagation()}>
              {/* Instellingen (oppervlakte, voeg, hoeken, toebehoren) —
                   wens Franky 19/8: de aparte kopbalk (foto, naam, prijs,
                   Sluit) zit nu mee IN dit vak, alles in één blok.
                   In de 'overzicht'-fase (na Toevoegen) verdwijnt dit vak. */}
              {popupFase === 'instellingen' && (
              <div className="overflow-y-auto bg-[#faf7f3] rounded-xl border border-gray-100 flex flex-col">
                <div className="p-4 space-y-4">

                  {/* Steen-kop + Toevoegen + Sluit */}
                  <div className="flex items-center justify-between gap-2 pb-2 sm:pb-3 border-b border-[#e6ded4]">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={calcProduct.images[0]} alt={calcProduct.name} className="w-10 h-8 sm:w-16 sm:h-12 rounded-lg object-cover" />
                      <div className="min-w-0">
                        <p className="font-bold text-sm sm:text-base text-[#1a1a1a] truncate">{calcProduct.name}</p>
                        <p className="text-xs text-[#8a7e74]">€{calcProduct.pricePerM2}/m² · <span className="hidden sm:inline">{t('v6.steen')} </span>{stonesInQuote}/5<span className="hidden sm:inline"> {t('v6.inDezeOfferte')}</span></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => { addAnotherStone(); setPopupFase('overzicht'); }}
                        disabled={stonesInQuote >= MAX_STONES}
                        className="px-3.5 py-1.5 bg-[#c47a3a] hover:bg-[#a8643a] text-white text-xs sm:text-sm font-bold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={stonesInQuote >= MAX_STONES ? 'Maximum bereikt — 5 stenen in deze offerte' : `Voeg ${calcProduct.name} (${area} m²) toe aan uw offerte — u ziet daarna het overzicht`}
                      >
                        {t('v6.toevoegen')}
                      </button>
                      <button onClick={() => setPopupFase('overzicht')} title="Sluit alleen dit instellingen-vak — de berekening blijft staan" className="px-3 py-1.5 text-sm font-bold text-[#8a7e74] hover:text-[#1a1a1a] border border-gray-200 rounded-lg bg-white">✕<span className="hidden sm:inline"> {t('v6.sluit')}</span></button>
                    </div>
                  </div>

                  {/* Oppervlakte */}
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-bold uppercase tracking-wide text-[#4a4a4a]">{t('calc.area')}</span>
                      <span className="text-[10px] text-[#8a7e74]">{t('calc.areaLabel')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="range" min={1} max={500} value={area} onChange={e => setArea(Math.max(1, +e.target.value))} className="flex-1" />
                      <div className="flex items-center gap-1.5">
                        <input type="number" min={1} max={9999} value={area}
                          onChange={e => {
                            // Laat de gebruiker vrij typen — clamp pas op blur.
                            // Voorheen blokkeerde `|| 1` het wissen van het laatste
                            // cijfer waardoor je niet over kon typen naar een nieuw getal.
                            const n = parseInt(e.target.value, 10);
                            setArea(Number.isFinite(n) ? Math.min(9999, Math.max(0, n)) : 0);
                          }}
                          onFocus={e => e.target.select()}
                          onBlur={() => { if (area < 1) setArea(1); }}
                          className="w-20 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-center font-bold text-sm focus:outline-none focus:border-[#c47a3a]" />
                        <span className="text-xs font-semibold text-[#8a7e74]">m²</span>
                      </div>
                    </div>
                  </div>

                  {/* Voegbreedte */}
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wide text-[#4a4a4a] block mb-1.5">{t('calc.jointWidth')}</span>
                    {/* Gsm (Franky 19/8): alle voeg-knoppen op één lijn — kort
                         label 'Geen' en compactere knoppen op smalle schermen */}
                    <div className="flex flex-nowrap sm:flex-wrap gap-1 sm:gap-1.5">
                      {[0, 5, 8, 10, 12, 15].map(w => (
                        <button key={w} onClick={() => setJointWidth(w)}
                          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-medium transition-all whitespace-nowrap ${jointWidth === w ? 'bg-[#c47a3a] text-white' : 'bg-white border border-gray-200 text-[#4a4a4a] hover:border-gray-300'}`}>
                          {w === 0 ? (<><span className="sm:hidden">Geen</span><span className="hidden sm:inline">{t('calc.noJoint')}</span></>) : `${w}mm`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Hoekstenen + Snijverlies */}
                  <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wide text-[#4a4a4a] block mb-0.5">{t('calc.corners')}<Vraagje tekst="Buitenhoeken × hoogte per hoek (m) — hoekstukken rekenen automatisch mee." /></span>
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] text-[#8a7e74]">
                          {t('calc.cornerCount')}<Vraagje tekst={t('v6.tipHoeken')} />
                        </label>
                        <input
                          type="number" min={0} max={50}
                          value={cornerCount}
                          onChange={e => setCornerCount(Math.max(0, Math.min(50, +e.target.value || 0)))}
                          className="w-12 px-1.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-center focus:outline-none focus:border-[#c47a3a]"
                        />
                        <label className="text-[10px] text-[#8a7e74]">
                          {t('calc.cornerHeight')}<Vraagje tekst={t('v6.tipHoogte')} />
                        </label>
                        <input
                          type="number" min={0} max={20} step={0.1}
                          value={cornerLength}
                          onChange={e => setCornerLength(Math.max(0, Math.min(20, +e.target.value || 0)))}
                          className="w-12 px-1.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-center focus:outline-none focus:border-[#c47a3a]"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-[#4a4a4a]">{t('calc.cuttingLoss')}<Vraagje tekst={t('v6.tipSnijverlies')} /></span>
                      <span className="text-sm font-bold text-[#c47a3a]">{cuttingLoss}%</span>
                      <span className="text-[10px] text-[#8a7e74]">{t('calc.fixedLoss')}</span>
                    </div>
                  </div>

                  {/* Toebehoren */}
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Toggle checked={includeGlue} onChange={setIncludeGlue} />
                      <span className="text-xs font-medium">{t('calc.includeGlue')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Toggle checked={includeGrout} onChange={setIncludeGrout} />
                      <span className="text-xs font-medium">{t('calc.includeGrout')}</span>
                    </label>
                    {includeGrout && (
                      <div className="flex gap-1.5">
                        {['grijs', 'wit', 'antraciet', 'zand'].map(c => (
                          <button key={c} onClick={() => setGroutColor(c)}
                            className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all ${groutColor === c ? 'bg-[#c47a3a] text-white' : 'bg-white border border-gray-200 text-[#4a4a4a]'}`}>
                            {groutColorMap[c]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden p-4 pt-2 mt-auto border-t border-gray-200/70">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74] block mb-1.5">
                    {t('calc.stoneStrip')}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {products.map(p => (
                      <button
                        key={`mini-${p.id}`}
                        onClick={() => { setCalcProductId(p.id); setSelectedProductId(p.id); }}
                        title={`${p.name} — €${p.pricePerM2}/m²`}
                        className={`h-9 w-12 rounded overflow-hidden border-2 transition-all ${
                          calcProductId === p.id ? 'border-[#c47a3a] scale-105' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>


              )}

              {/* De brede toevoeg-balk stond hier — verplaatst naar de kop
                   naast 'Sluit' (wens Franky 19/8: meer plaats op de gsm). */}

              {/* Berekening + offerte aanvragen */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col">

                {/* Results header — in de overzicht-fase met knop voor een
                     volgende steen (de instellingen zijn dan verborgen) */}
                <div className="bg-[#1a1a1a] text-white px-5 py-4 flex items-center justify-between gap-2">
                  <div className="order-last flex items-center gap-2 flex-shrink-0">
                    {/* Wens Franky (19/8): '+ Nieuwe steen' ALTIJD zichtbaar in
                         de berekening-balk, ook als het instellingen-vak open staat */}
                    <button
                      onClick={() => { setStoneModal(false); document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' }); }}
                      disabled={stonesInQuote > MAX_STONES}
                      className="px-3 py-1.5 bg-[#c47a3a] hover:bg-[#a8643a] text-white text-xs font-bold rounded-lg disabled:opacity-40"
                      title="Kies uw volgende gevelsteen uit de lijst"
                    >{t('v6.nieuweSteen')}</button>
                    {/* Wens Franky (19/8): rechtstreeks terug naar het 3D-huisje */}
                    <button
                      onClick={() => { setStoneModal(false); document.getElementById('huis3d')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-lg border border-white/25"
                      title="Sluit dit venster en bekijk uw stenen op het 3D-huisje"
                    >{t('v6.3d')}</button>
                    <button onClick={() => setStoneModal(false)} title="Sluit dit venster" className="px-2.5 py-1.5 text-xs font-bold text-white/70 hover:text-white border border-white/25 rounded-lg">✕</button>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold flex items-center gap-2">
                      {t('calc.yourCalc')}
                      {apiLoading && <Loader2 size={12} className="animate-spin text-gray-400" />}
                      {apiResult && !apiLoading && <span className="text-[9px] text-green-400 font-normal">API</span>}
                    </h3>
                    <p className="text-xs text-gray-400 truncate mt-0.5">
                      {calcProduct.name} — {displayCalc.area} m²
                      {calcProduct.deliveryWeeks != null && (
                        <span className="ml-2 text-[#c47a3a]">· levertermijn ±{calcProduct.deliveryWeeks} weken</span>
                      )}
                    </p>
                  </div>
                  {/* Prijs uit de kopbalk verwijderd (Franky 19/8) — het
                       totaal staat duidelijk onderaan bij 'Totaal'. */}
                </div>

                {/* v3.2.109 — DiscountSavingsStrip op de calculator-
                    voorkant verwijderd. Filip: 'met kortingen strooien
                    is gevaarlijk dus haal dat weg van die voorkant en
                    zet dit misschien alleen in de offerte als er werkelijk
                    een kortings product is gekozen en korting is
                    toegepast'. De server-side berekening van korting in
                    offertes blijft intact (zie /api/calculator + quotes). */}

                {/* Line items */}
                <div className="px-5 py-4 space-y-0">
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-50">
                    <span className="text-sm text-[#4a4a4a] min-w-0 truncate">{t('calc.brickStrips')} <span className="text-[#8a7e74] text-xs">({displayCalc.totalStones} st)</span></span>
                    <span className="text-sm font-bold text-[#c47a3a] flex-shrink-0 ml-3">€{Math.round(displayCalc.stonePrice).toLocaleString()}</span>
                  </div>
                  {displayCalc.totalCornerPieces > 0 && (
                    <div className="flex justify-between items-center py-2.5 border-b border-gray-50">
                      <span className="text-sm text-[#4a4a4a] min-w-0 truncate">{t('calc.cornerStones')} <span className="text-[#8a7e74] text-xs">({displayCalc.totalCornerPieces} st)</span></span>
                      <span className="text-sm font-bold text-[#c47a3a] flex-shrink-0 ml-3">€{Math.round(displayCalc.cornerPrice).toLocaleString()}</span>
                    </div>
                  )}
                  {includeGlue && (
                    <div className="flex justify-between items-center py-2.5 border-b border-gray-50">
                      <span className="text-sm text-[#4a4a4a] min-w-0 truncate">{t('calc.glue')} <span className="text-[#8a7e74] text-xs">({displayCalc.glueBags} {t('calc.bags')})</span></span>
                      <span className="text-sm font-bold text-[#c47a3a] flex-shrink-0 ml-3">€{displayCalc.gluePrice}</span>
                    </div>
                  )}
                  {includeGrout && (
                    <div className="flex justify-between items-center py-2.5 border-b border-gray-50">
                      <span className="text-sm text-[#4a4a4a] min-w-0 truncate">{t('calc.grout')} ({groutColorMap[groutColor]})</span>
                      <span className="text-sm font-bold text-[#c47a3a] flex-shrink-0 ml-3">€{displayCalc.groutPrice}</span>
                    </div>
                  )}

                  {/* v5 blok 4: eerder toegevoegde stenen in deze offerte */}
                  {quoteLines.length > 0 && (
                    <div className="mt-3 border border-[#c47a3a]/20 rounded-xl overflow-hidden">
                      <p className="px-3 py-2 bg-[#c47a3a]/10 text-[11px] font-bold uppercase tracking-wide text-[#8a4423]">
                        {t('v6.reedsInOfferte')} ({quoteLines.length})
                      </p>
                      {/* V6 (fix Franky 19/8): de GANSE balk is klikbaar en
                           opent de instellingen van die steen bovenaan */}
                      {quoteLines.map((l, i) => {
                        const rijOpen = popupFase === 'instellingen' && l.productId === calcProductId;
                        return (
                        <div key={i}
                          onClick={() => bewerkQuoteLine(i)}
                          className={`flex items-center justify-between px-3 py-2 border-t text-sm cursor-pointer ${rijOpen ? 'border-[#c47a3a]/30 bg-[#c47a3a]/10' : 'border-gray-50 hover:bg-[#c47a3a]/5'}`}
                          title={rijOpen ? 'Deze steen staat hierboven open — klik om weer te sluiten' : 'Klik om deze steen terug te openen en zijn instellingen (oppervlakte, voeg, hoeken) aan te passen'}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span className={`w-5 h-5 rounded-full flex-shrink-0 bg-[#c47a3a] text-white text-[10px] font-bold flex items-center justify-center ${rijOpen ? 'animate-pulse' : ''}`}>{i + 1}</span>
                            <span className="w-3 h-3 rounded-full flex-shrink-0 border border-black/10" style={{ backgroundColor: l.colorHex }} />
                            <span className="truncate">{l.productName} <span className="text-xs text-[#8a7e74]">({l.area} m²)</span> <span className="text-[#c47a3a]">✎</span></span>
                            {rijOpen && <span className="text-[10px] font-bold text-[#8a4423] flex-shrink-0">● staat hierboven open</span>}
                          </span>
                          <span className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className="font-bold text-[#c47a3a]">€{Math.round(l.totalPrice).toLocaleString()}</span>
                            <button onClick={(e) => { e.stopPropagation(); removeQuoteLine(i); }} className="text-[#8a7e74] hover:text-red-600 text-xs font-bold px-1" title="Steen uit offerte verwijderen">✕</button>
                          </span>
                        </div>
                      ); })}
                      {/* Wens Franky (19/8): duidelijk bolletje bij de steen
                           die nu bovenaan openstaat om aan te passen */}
                      {popupFase === 'instellingen' && actieveTeltMee && (
                        <div className="flex items-center justify-between px-3 py-2 border-t border-[#c47a3a]/30 bg-[#c47a3a]/10 text-sm">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-5 h-5 rounded-full bg-[#c47a3a] text-white text-[10px] font-bold flex items-center justify-center animate-pulse flex-shrink-0">{quoteLines.length + 1}</span>
                            <span className="w-3 h-3 rounded-full flex-shrink-0 border border-black/10" style={{ backgroundColor: calcProduct.colorHex }} />
                            <span className="truncate font-semibold">{calcProduct.name} <span className="text-xs text-[#8a7e74]">({area} m²)</span></span>
                          </span>
                          <span className="text-[11px] font-bold text-[#8a4423] flex-shrink-0 ml-2">{t('v6.staatOpen')}</span>
                        </div>
                      )}
                    </div>
                  )}


                  {/* '+ Vul nog een gevelsteen in' verwijderd (Franky 19/8):
                       de knop '+ Nieuwe steen' in de zwarte balk doet dit. */}

                  {/* Total */}
                  <div className="bg-[#c47a3a]/5 rounded-xl p-4 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-[#1a1a1a]">
                        {t('calc.totalExcl')}{quoteLines.length > 0 && <span className="text-xs font-normal text-[#8a7e74]"> ({stonesInQuote} stenen)</span>}
                      </span>
                      <span className="text-lg font-bold text-[#c47a3a]">€{Math.round(combinedTotal).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#8a7e74] mt-0.5">
                      <span>{t('calc.inclVat')}</span>
                      <span>€{Math.round(combinedTotal * 1.21).toLocaleString()}</span>
                    </div>
                    {/* Vaste regel op elke offerte (roadmap blok 4.5) */}
                    <p className="text-[10px] text-[#8a7e74] mt-2 italic">Werkuren van de plaatsingen niet inbegrepen. Alle prijzen zijn puur materiaal.</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="px-5 pb-5 pt-2 space-y-2.5">
                  {/* "Offerte aanvragen" — opent altijd het formulier zodat de
                       klant zijn/haar gegevens kan bevestigen of aanvullen. Voor
                       ingelogde gebruikers worden de velden geprefild vanuit
                       hun profiel. */}
                  {!showQuoteForm && !qfSuccess && (
                    <>
                      <button
                        onClick={handleRequestQuote}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-[#c47a3a] text-white text-sm font-semibold rounded-xl hover:bg-[#b06a2a] transition-colors"
                      >
                        <FileTextIcon size={14} />
                        {t('v6.offerteMetBerekening')}
                      </button>
                      {/* v3.2.66 — 'Offerte opslaan in uw account' verwijderd.
                           Filip: 'verwart mensen die nog geen account hebben'.
                           Eén CTA volstaat: 'Offerte aanvragen' opent het
                           formulier dat voor ingelogden auto-gevuld wordt
                           vanuit hun profiel; voor nieuwe bezoekers worden
                           account + offerte in één stap aangemaakt. */}
                      {quoteError && (
                        <p className="text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-center">{quoteError}</p>
                      )}
                    </>
                  )}

                  {/* Inline Quote Form */}
                  {showQuoteForm && !qfSuccess && (
                    <div id="quote-form-anchor" className="border border-[#c47a3a]/20 bg-[#faf7f3] rounded-xl p-4 space-y-3">
                      {/* V6 punt 9: eerst kiezen wie je bent — BTW-velden alleen bij bedrijf */}
                      <div className="flex gap-2">
                        {(['particulier', 'bedrijf'] as const).map(tp => (
                          <button
                            key={tp}
                            onClick={() => setQfType(tp)}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition-colors capitalize ${
                              qfType === tp ? 'border-[#c47a3a] bg-[#c47a3a] text-white' : 'border-gray-200 bg-white text-[#4a4a4a] hover:border-[#c47a3a]/50'
                            }`}
                          >
                            {tp === 'particulier' ? '👤 Particulier' : '🏢 Bedrijf'}
                          </button>
                        ))}
                      </div>
                      {/* V6 punt 10: binnen welke termijn? Compact (Franky 19/8):
                           korte labels + kleine knoppen -> max twee regels,
                           titel en knoppen dicht op elkaar (gsm én desktop). */}
                      <div className="bg-white border border-gray-200 rounded-lg p-2.5 -mx-1">
                        <p className="text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">
                          {t('v6.termijnVraag')} <span className="text-red-500">*</span>
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {([['3m', t('v6.t3m')], ['6m', t('v6.t6m')], ['1j', t('v6.t1j')], ['meer', t('v6.tLater')], ['info', t('v6.tInfo')]] as const).map(([v, l]) => (
                            <button key={v} onClick={() => setQfTermijn(v)}
                              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors whitespace-nowrap ${
                                qfTermijn === v ? 'border-[#c47a3a] bg-[#c47a3a] text-white' : 'border-gray-200 bg-white text-[#4a4a4a] hover:border-[#c47a3a]/50'
                              }`}>{l}</button>
                          ))}
                        </div>
                        {qfErrors.termijn && <p className="text-[10px] text-red-500 mt-1">{qfErrors.termijn}</p>}
                      </div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-bold text-[#1a1a1a]">{user ? t('v6.bevestigGegevens') : t('v6.uwGegevens')}</h4>
                        <button onClick={() => setShowQuoteForm(false)} className="text-xs text-[#8a7e74] hover:text-[#4a4a4a]">{t('v6.sluiten')}</button>
                      </div>
                      {user && (
                        <p className="text-[11px] text-[#8a7e74] bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                          ℹ️ Uw profielgegevens zijn ingevuld. Vul aan of pas aan waar nodig — deze gegevens worden opgeslagen bij deze offerte.
                        </p>
                      )}

                      {/* Naam */}
                      <div>
                        <label className="text-xs font-medium text-[#4a4a4a] block mb-1">Naam *</label>
                        <input type="text" value={qfName} onChange={e => setQfName(e.target.value)}
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#c47a3a] ${qfErrors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                          placeholder="Jan Janssens" />
                        {qfErrors.name && <p className="text-[10px] text-red-500 mt-0.5">{qfErrors.name}</p>}
                      </div>

                      {/* Email */}
                      <div>
                        <label className="text-xs font-medium text-[#4a4a4a] block mb-1">E-mail *</label>
                        <input type="email" value={qfEmail} onChange={e => setQfEmail(e.target.value)}
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#c47a3a] ${qfErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                          placeholder="jan@voorbeeld.be" />
                        {qfErrors.email && <p className="text-[10px] text-red-500 mt-0.5">{qfErrors.email}</p>}
                      </div>

                      {/* Telefoon */}
                      <div>
                        <label className="text-xs font-medium text-[#4a4a4a] block mb-1">Telefoon *</label>
                        <input type="tel" value={qfPhone} onChange={e => setQfPhone(e.target.value)}
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#c47a3a] ${qfErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                          placeholder="+32 4XX XX XX XX" />
                        {qfErrors.phone && <p className="text-[10px] text-red-500 mt-0.5">{qfErrors.phone}</p>}
                      </div>

                      {/* Adres */}
                      <div>
                        <label className="text-xs font-medium text-[#4a4a4a] block mb-1">Adres *</label>
                        <input type="text" value={qfAddress} onChange={e => setQfAddress(e.target.value)}
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#c47a3a] ${qfErrors.address ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                          placeholder="Kerkstraat 1" />
                        {qfErrors.address && <p className="text-[10px] text-red-500 mt-0.5">{qfErrors.address}</p>}
                      </div>

                      {/* Postcode + Gemeente */}
                      <div>
                        <label className="text-xs font-medium text-[#4a4a4a] block mb-1">Postcode + Gemeente *</label>
                        <input type="text" value={qfPostalCity} onChange={e => setQfPostalCity(e.target.value)}
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#c47a3a] ${qfErrors.postalCity ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                          placeholder="2000 Antwerpen" />
                        {qfErrors.postalCity && <p className="text-[10px] text-red-500 mt-0.5">{qfErrors.postalCity}</p>}
                      </div>

                      {/* V6 punt 9: bedrijfsvelden alleen zichtbaar wanneer
                           bovenaan 'Bedrijf' gekozen is. */}
                      {qfType === 'bedrijf' && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 -mx-1">
                        <p className="text-[11px] font-semibold text-[#8a7e74] uppercase tracking-wide mb-2">
                          Bedrijfsgegevens
                        </p>
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs font-medium text-[#4a4a4a] block mb-1">Bedrijfsnaam <span className="text-[10px] text-[#8a7e74]">(optioneel)</span></label>
                            <input
                              type="text"
                              value={qfCompany}
                              onChange={e => {
                                setQfCompany(e.target.value);
                                if (e.target.value.trim() || qfBtw.trim()) setQfType('bedrijf');
                                else if (!qfBtw.trim()) setQfType('particulier');
                              }}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#c47a3a] ${qfErrors.company ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                              placeholder="Bedrijf BV"
                            />
                            {qfErrors.company && <p className="text-[10px] text-red-500 mt-0.5">{qfErrors.company}</p>}
                          </div>
                          <div>
                            <label className="text-xs font-medium text-[#4a4a4a] block mb-1">BTW-nummer <span className="text-[10px] text-[#8a7e74]">(optioneel)</span></label>
                            <input
                              type="text"
                              value={qfBtw}
                              onChange={e => {
                                setQfBtw(e.target.value);
                                if (e.target.value.trim() || qfCompany.trim()) setQfType('bedrijf');
                                else if (!qfCompany.trim()) setQfType('particulier');
                              }}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#c47a3a] ${qfErrors.btw ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                              placeholder="BE0XXX.XXX.XXX"
                            />
                            {qfErrors.btw && <p className="text-[10px] text-red-500 mt-0.5">{qfErrors.btw}</p>}
                          </div>
                          <p className="text-[10px] text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1.5 mt-1">
                            ✓ B2B-modus — u ontvangt een factuur excl. BTW (verlegging mogelijk).
                          </p>
                        </div>
                      </div>
                      )}

                      {/* ─── Plaatsing — wie installeert? ─────────────────── */}
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-xs font-bold text-emerald-900 uppercase tracking-wide mb-1">
                          {t('v6.plaatsingKop')}
                        </p>
                        <p className="text-[10px] text-emerald-800 mb-2">
                          {t('v6.plaatsingVraag')}
                        </p>
                        <div className="space-y-1.5">
                          <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/60">
                            <input
                              type="radio" name="qf-installer"
                              checked={qfInstaller === 'installer'}
                              onChange={() => setQfInstaller('installer')}
                              className="w-4 h-4 accent-emerald-600"
                            />
                            <span className="text-sm text-[#1a1a1a]">
                              <strong>{t('v6.plOptInstall')}</strong>
                              <span className="block text-[10px] text-[#8a7e74]">{t('v6.plOptInstallSub')}</span>
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/60">
                            <input
                              type="radio" name="qf-installer"
                              checked={qfInstaller === 'self'}
                              onChange={() => setQfInstaller('self')}
                              className="w-4 h-4 accent-emerald-600"
                            />
                            <span className="text-sm text-[#1a1a1a]">
                              {t('v6.plOptZelf')}
                              <span className="block text-[10px] text-[#8a7e74]">{t('v6.plOptZelfSub')}</span>
                            </span>
                          </label>
                          {/* V6 punt 11: derde keuze — via de eigen groothandelaar */}
                          <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/60">
                            <input
                              type="radio" name="qf-installer"
                              checked={qfInstaller === 'groothandel'}
                              onChange={() => setQfInstaller('groothandel')}
                              className="w-4 h-4 accent-emerald-600"
                            />
                            <span className="text-sm text-[#1a1a1a]">
                              {t('v6.plOptGh')}
                              <span className="block text-[10px] text-[#8a7e74]">{t('v6.plOptGhSub')}</span>
                            </span>
                          </label>
                          {/* v3.2.70 — 'Een vriend of kennis plaatst voor mij'
                               verwijderd op verzoek van Filip. Twee opties zijn
                               voldoende: zelf doen (alleen materiaal) of een
                               installateur regelen via ons. */}
                        </div>

                        {/* V6 punten 13-15: installateurs uit uw regio (±50 km) */}
                        {qfInstaller === 'installer' && (
                          <div className="mt-2 bg-white/70 border border-emerald-200 rounded-lg p-2.5">
                            {!qfPostcode4 ? (
                              <p className="text-[11px] text-emerald-900">
                                {t('v6.instVulPostcode')}
                              </p>
                            ) : qfInstLaden ? (
                              <p className="text-[11px] text-[#8a7e74]">{t('v6.instZoeken')}</p>
                            ) : qfInstLijst && qfInstLijst.length > 0 ? (
                              <>
                                <p className="text-[11px] font-bold text-emerald-900 mb-1.5">
                                  {t('v6.instLijstIntro')}
                                </p>
                                <div className="space-y-1">
                                  {qfInstLijst.map(i => (
                                    <label key={i.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-emerald-50">
                                      <input
                                        type="checkbox"
                                        checked={qfInstSel.includes(i.id)}
                                        onChange={() => setQfInstSel(sel =>
                                          sel.includes(i.id) ? sel.filter(x => x !== i.id) : [...sel, i.id])}
                                        className="w-4 h-4 accent-emerald-600"
                                      />
                                      <span className="text-xs text-[#1a1a1a]">
                                        <strong>{i.company_name.replace(' (demo)', '')}</strong>
                                        <span className="text-[#8a7e74]"> — {i.city} · {i.afstand_km} km{i.contact_phone ? ` · ${i.contact_phone}` : ''}</span>
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                {t('v6.instGeen')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* V6 punt 15: eigen (niet-aangesloten) groothandel opgeven */}
                        {qfInstaller === 'groothandel' && (
                          <div className="mt-2 bg-white/70 border border-emerald-200 rounded-lg p-2.5 space-y-1.5">
                            <p className="text-[11px] font-bold text-emerald-900">
                              {t('v6.ghIntro')}
                            </p>
                            <div className="grid grid-cols-2 gap-1.5">
                              <input type="text" value={qfGhNaam} onChange={e => setQfGhNaam(e.target.value)}
                                placeholder={t('v6.ghNaam')}
                                className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#c47a3a]" />
                              <input type="text" value={qfGhPlaats} onChange={e => setQfGhPlaats(e.target.value)}
                                placeholder={t('v6.ghGemeente')}
                                className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#c47a3a]" />
                            </div>
                            <input type="email" value={qfGhEmail} onChange={e => setQfGhEmail(e.target.value)}
                              placeholder={t('v6.ghEmail')}
                              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#c47a3a]" />
                          </div>
                        )}

                        {/* V6 punt 17: hoe loopt het geld? */}
                        <p className="text-[10px] text-emerald-800/80 mt-2 border-t border-emerald-200 pt-1.5">
                          {t('v6.geldstroom')}
                        </p>
                        {qfErrors.installer && <p className="text-[10px] text-red-500 mt-1">{qfErrors.installer}</p>}
                      </div>

                      {/* Server error */}
                      {qfServerError && (
                        <p className="text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-center">{qfServerError}</p>
                      )}

                      {/* Submit */}
                      <button
                        onClick={handleQuoteFormSubmit}
                        disabled={qfSubmitting}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-[#c47a3a] text-white text-sm font-semibold rounded-xl hover:bg-[#b06a2a] transition-colors disabled:opacity-50"
                      >
                        {qfSubmitting ? <Loader2 size={14} className="animate-spin" /> : <FileTextIcon size={14} />}
                        {qfSubmitting ? 'Verzenden…' : 'Offerte aanvragen'}
                      </button>
                    </div>
                  )}

                  {/* Success message */}
                  {qfSuccess && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                        <p className="text-sm text-green-700 font-semibold">✓ Uw offerte is verzonden!</p>
                      </div>
                      <p className="text-xs text-green-700 leading-relaxed">
                        We hebben een <strong>inlogcode</strong> gestuurd naar <strong>{qfEmail}</strong>.
                        Vul de code in op de inlogpagina om uw offerte te bekijken in uw persoonlijk dashboard.
                      </p>
                      <p className="text-[11px] text-green-600 bg-white border border-green-200 rounded px-2 py-1.5">
                        💡 Geen mail ontvangen? Geen zorgen — uw offerte staat veilig opgeslagen en we nemen contact met u op.
                        Voor testen: code is <strong>123456</strong>.
                      </p>
                      <p className="text-[10px] text-green-500">U wordt doorgestuurd…</p>
                    </div>
                  )}
                </div>

                <p className="text-[10px] text-[#8a7e74] text-center pb-4 px-5">{t('calc.disclaimer')}</p>
              </div>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* V6-fix (melding Franky): sinds de verbouw naar één hoofdblok was de
          pagina hieronder leeg — op hoge/uitgezoomde schermen gaf dat een
          groot gat boven de footer (main rekt met min-h-screen mee). Deze
          strook vult dat met inhoud in plaats van leegte. */}
      <section className="bg-[#faf7f3] py-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white border border-[#e6ded4] rounded-xl p-5">
              <p className="text-2xl mb-1.5">🏭</p>
              <p className="font-bold text-[#1a1a1a] mb-1">{t('v6.uspFabrikantKop')}</p>
              <p className="text-sm text-[#8a7e74]">{t('v6.uspFabrikant')}</p>
            </div>
            <div className="bg-white border border-[#e6ded4] rounded-xl p-5">
              <p className="text-2xl mb-1.5">🔨</p>
              <p className="font-bold text-[#1a1a1a] mb-1">{t('v6.uspPlaatsingKop')}</p>
              <p className="text-sm text-[#8a7e74]">{t('v6.uspPlaatsing')}</p>
            </div>
            <div className="bg-white border border-[#e6ded4] rounded-xl p-5">
              <p className="text-2xl mb-1.5">📦</p>
              <p className="font-bold text-[#1a1a1a] mb-1">{t('v6.uspLeveringKop')}</p>
              <p className="text-sm text-[#8a7e74]">{t('v6.uspLevering')}</p>
            </div>
          </div>
          <Link to="/inspiratie"
            className="mt-4 flex items-center justify-between gap-4 bg-[#1a1a1a] text-white rounded-xl px-5 py-4 hover:bg-[#2a2a2a] transition-colors group">
            <span>
              <span className="font-bold block">{t('v6.inspiratieKop')}</span>
              <span className="text-sm text-white/70">{t('v6.inspiratieSub')}</span>
            </span>
            <span className="text-[#c47a3a] text-xl group-hover:translate-x-1 transition-transform">→</span>
          </Link>
          {/* V6 punt 24: zelf-aanmelding voor professionelen, zichtbaar vooraan */}
          <p className="mt-3 text-center text-sm text-[#8a7e74]">
            {t('v6.kortingCta')}{' '}
            <Link to="/reseller-aanvraag" className="text-[#c47a3a] font-semibold hover:underline">
              {t('v6.kortingLink')}
            </Link>
          </p>
        </div>
      </section>

      {/* Inspiration gallery moved to dedicated /inspiratie page — kept off
          the home/products page to avoid duplicate content (also better for SEO). */}

      <LoginModal
        isOpen={loginModalOpen}
        onClose={() => { setLoginModalOpen(false); pendingQuoteRef.current = false; }}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* ─── Favorieten toast — bevestiging onderaan scherm ──────────────── */}
      {favToastMsg && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#1a1a1a] text-white px-4 py-2.5 rounded-lg shadow-2xl text-sm font-medium animate-fade-in flex items-center gap-2 max-w-[90vw]"
          role="status"
          aria-live="polite"
        >
          {favToastMsg}
        </div>
      )}
      {/* v3.2.100 — ProductDetailModal werd geïmporteerd maar nooit gerenderd.
           Het info-icoontje (bottom-right op product-tegels) riep setDetailProduct
           op zonder dat de modal in de DOM stond, waardoor er visueel niks
           gebeurde ('alles is wit' aldus Filip — letterlijk: er gebeurt niets).
           Nu rendert de modal correct conditioneel op detailProduct state. */}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onUseInCalculator={(productId) => {
            setDetailProduct(null);
            setCalcProductId(productId);
            setSelectedProductId(productId);
            (setPopupFase('instellingen'), setStoneModal(true));
          }}
        />
      )}
    </div>
  );
}
